import React from 'react';
import './PricingBreakdown.css';
import { DEPLOYMENT_MODELS } from '../utils/pricingEngine';

function PricingBreakdown({ results, inputs }) {
  const model = DEPLOYMENT_MODELS[inputs.deploymentModel];
  const { warnings } = results;

  // Format currency
  const fmt = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  const hasErrors = warnings.errors.length > 0;
  const hasWarnings = warnings.warnings.length > 0;

  return (
    <div className="pricing-breakdown">
      <h2>Pricing Breakdown</h2>

      {/* Alerts */}
      {(hasErrors || hasWarnings || warnings.info.length > 0) && (
        <div className="alerts-section">
          {warnings.errors.map((err, idx) => (
            <div key={`error-${idx}`} className="alert alert-error">
              <span className="alert-icon">🚫</span>
              <span>{err.message}</span>
            </div>
          ))}
          {warnings.warnings.map((warn, idx) => (
            <div key={`warning-${idx}`} className="alert alert-warning">
              <span className="alert-icon">⚠️</span>
              <span>{warn.message}</span>
            </div>
          ))}
          {warnings.info.map((info, idx) => (
            <div key={`info-${idx}`} className="alert alert-info">
              <span className="alert-icon">ℹ️</span>
              <span>{info.message}</span>
            </div>
          ))}
        </div>
      )}

      {/* Summary Card */}
      <div className={`summary-card ${hasErrors ? 'has-errors' : ''}`}>
        <div className="summary-header">
          <div className="arr-display">
            <div className="arr-label">Annual Recurring Revenue</div>
            <div className="arr-value">{fmt(results.annualRecurringRevenue)}</div>
          </div>
          <div className="mrr-display">
            <span className="mrr-label">MRR:</span>
            <span className="mrr-value">{fmt(results.monthlyRecurringRevenue)}/mo</span>
          </div>
        </div>
        <div className="summary-details">
          <div className="summary-row">
            <span>Seats Required:</span>
            <span><strong>{results.seats}</strong></span>
          </div>
          <div className="summary-row">
            <span>Margin:</span>
            <span className={results.marginPercentage < 25 ? 'text-warning' : 'text-success'}>
              {fmt(results.margin)} ({results.marginPercentage.toFixed(1)}%)
            </span>
          </div>
          <div className="summary-row">
            <span>Total Costs:</span>
            <span>{fmt(results.totalCosts)}</span>
          </div>
          {results.setupFee > 0 && (
            <div className="summary-row">
              <span>One-time Setup Fee:</span>
              <span className="text-accent">{fmt(results.setupFee)}</span>
            </div>
          )}
        </div>
      </div>

      {/* Revenue Breakdown */}
      <div className="breakdown-card">
        <h3>Revenue Breakdown</h3>
        <table className="breakdown-table">
          <tbody>
            <tr>
              <td>Seat Licenses (includes all usage)</td>
              <td className="text-secondary">{results.seats} × {fmt(model.pricePerSeat)}</td>
              <td className="amount">{fmt(results.seatRevenue)}</td>
            </tr>
            {results.integrationPrice > 0 && (
              <tr>
                <td>Integration Fees</td>
                <td className="text-secondary">
                  {results.markupBand.label}
                </td>
                <td className="amount">{fmt(results.integrationPrice)}</td>
              </tr>
            )}
            <tr className="subtotal-row">
              <td colSpan="2"><strong>Subtotal</strong></td>
              <td className="amount"><strong>{fmt(results.subtotal)}</strong></td>
            </tr>
            {results.contractDiscountAmount > 0 && (
              <tr className="discount-row">
                <td>Contract Discount</td>
                <td className="text-secondary">{inputs.contractYears} year term</td>
                <td className="amount text-success">-{fmt(results.contractDiscountAmount)}</td>
              </tr>
            )}
            {results.additionalDiscountAmount > 0 && (
              <tr className="discount-row">
                <td>Additional Discount</td>
                <td className="text-secondary">{inputs.discount}%</td>
                <td className="amount text-success">-{fmt(results.additionalDiscountAmount)}</td>
              </tr>
            )}
            {(results.contractDiscountAmount > 0 || results.additionalDiscountAmount > 0) && (
              <tr className="subtotal-row">
                <td colSpan="2"><strong>After Discounts</strong></td>
                <td className="amount"><strong>{fmt(results.totalAfterDiscounts)}</strong></td>
              </tr>
            )}
            {results.supportRevenue > 0 && (
              <tr>
                <td>Enterprise Support</td>
                <td className="text-secondary">
                  30% of MRR
                </td>
                <td className="amount">{fmt(results.supportRevenue)}</td>
              </tr>
            )}
            <tr className="subtotal-row">
              <td colSpan="2"><strong>Monthly Recurring Revenue (MRR)</strong></td>
              <td className="amount"><strong>{fmt(results.monthlyRecurringRevenue)}</strong></td>
            </tr>
            {results.professionalServicesRevenue > 0 && (
              <tr>
                <td>Professional Services (One-time)</td>
                <td className="text-secondary">
                  {inputs.professionalServices.hours}hrs × {fmt(inputs.professionalServices.rate)}
                </td>
                <td className="amount">{fmt(results.professionalServicesRevenue)}</td>
              </tr>
            )}
            <tr className="total-row">
              <td colSpan="2"><strong>Total Monthly Revenue</strong></td>
              <td className="amount"><strong>{fmt(results.totalMonthlyRevenue)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Cost Breakdown */}
      <div className="breakdown-card">
        <h3>Cost Breakdown</h3>
        <table className="breakdown-table">
          <tbody>
            <tr>
              <td>Bundled Usage (included in seats)</td>
              <td className="text-secondary">{results.seats} × $9.50</td>
              <td className="amount">{fmt(results.bundledCost)}</td>
            </tr>
            {results.integrationCost > 0 && (
              <tr>
                <td>Integration Costs</td>
                <td className="text-secondary">At cost</td>
                <td className="amount">{fmt(results.integrationCost)}</td>
              </tr>
            )}
            {results.supportCost > 0 && (
              <tr>
                <td>Support Costs</td>
                <td className="text-secondary">20% of support revenue</td>
                <td className="amount">{fmt(results.supportCost)}</td>
              </tr>
            )}
            {results.infraCost > 0 && (
              <tr>
                <td>Infrastructure (Dedicated Cloud)</td>
                <td className="text-secondary">Fixed monthly</td>
                <td className="amount">{fmt(results.infraCost)}</td>
              </tr>
            )}
            {results.professionalServicesCost > 0 && (
              <tr>
                <td>Professional Services Cost</td>
                <td className="text-secondary">
                  {inputs.professionalServices.hours}hrs × $100
                </td>
                <td className="amount">{fmt(results.professionalServicesCost)}</td>
              </tr>
            )}
            <tr className="total-row">
              <td colSpan="2"><strong>Total Monthly Costs</strong></td>
              <td className="amount"><strong>{fmt(results.totalCosts)}</strong></td>
            </tr>
          </tbody>
        </table>
      </div>

      {/* Usage Details */}
      <div className="breakdown-card">
        <h3>Usage Coverage</h3>
        <div className="usage-grid">
          <div className="usage-item">
            <div className="usage-label">Input Tokens</div>
            <div className="usage-value">{results.actualUsage.inputTokens}M requested</div>
            <div className="usage-breakdown">
              <div className="usage-bundled">{results.bundledUsage.inputTokens}M provided ({results.seats} seats)</div>
            </div>
          </div>

          <div className="usage-item">
            <div className="usage-label">Output Tokens</div>
            <div className="usage-value">{results.actualUsage.outputTokens}M requested</div>
            <div className="usage-breakdown">
              <div className="usage-bundled">{results.bundledUsage.outputTokens}M provided ({results.seats} seats)</div>
            </div>
          </div>

          <div className="usage-item">
            <div className="usage-label">Meeting Hours</div>
            <div className="usage-value">{results.actualUsage.meetingHours}hrs requested</div>
            <div className="usage-breakdown">
              <div className="usage-bundled">{results.bundledUsage.meetingHours}hrs provided ({results.seats} seats)</div>
            </div>
          </div>

          {inputs.integrations.connections > 0 && (
            <div className="usage-item">
              <div className="usage-label">Integrations</div>
              <div className="usage-value">
                {inputs.integrations.connections} connections
              </div>
              <div className="usage-breakdown">
                <div className="usage-bundled">{inputs.integrations.records.toLocaleString()} records</div>
                <div className="usage-bundled">{inputs.integrations.actions.toLocaleString()} actions</div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Pricing Band Info */}
      <div className="info-card">
        <h4>Pricing Information</h4>
        <p>
          <strong>{results.seats} seats</strong> required to cover all usage (no overages).
        </p>
        {results.integrationCost > 0 && (
          <p>
            Integration cost of <strong>{fmt(results.integrationCost)}</strong> priced with{' '}
            <strong>{results.markupBand.label}</strong>.
          </p>
        )}
        {results.minimumPrice > results.totalMonthlyRevenue && (
          <div className="minimum-price-warning">
            <p>
              <strong>⚠️ Minimum Acceptable Price:</strong> {fmt(results.minimumPrice)}
            </p>
            <p>Current quote is below the 1.2x cost floor. Adjust pricing or reduce discount.</p>
          </div>
        )}
      </div>

      {/* Export Options */}
      <div className="actions">
        <button
          className="action-btn"
          onClick={() => {
            const quoteData = {
              inputs,
              results,
              generatedAt: new Date().toISOString(),
            };
            const dataStr = JSON.stringify(quoteData, null, 2);
            const dataBlob = new Blob([dataStr], { type: 'application/json' });
            const url = URL.createObjectURL(dataBlob);
            const link = document.createElement('a');
            link.href = url;
            link.download = `tectra-quote-${Date.now()}.json`;
            link.click();
          }}
        >
          Export Quote (JSON)
        </button>
        <button
          className="action-btn secondary"
          onClick={() => window.print()}
        >
          Print Quote
        </button>
      </div>
    </div>
  );
}

export default PricingBreakdown;
